import React, { useRef, useState } from 'react';
import { logoApi } from '../utils/api';

const MAX_FILE_BYTES = 3.5 * 1024 * 1024;

export default function LogoUrlUploadField({
  value,
  onChange,
  uploadType = 'form',
  uploadIdValue,
  disabled = false,
  helperText,
}) {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  async function handleFilePick(event) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setUploadError(null);

    if (!file.type.startsWith('image/')) {
      setUploadError('Please choose an image file.');
      return;
    }

    if (file.size > MAX_FILE_BYTES) {
      setUploadError('Image is too large. Maximum size is about 3.5 MB.');
      return;
    }

    if (!uploadIdValue) {
      setUploadError('Missing form or workspace ID.');
      return;
    }

    setUploading(true);
    try {
      const dataUrl = await readFileAsDataUrl(file);
      const base64 = dataUrl.split(',')[1];
      if (!base64) {
        throw new Error('Could not read image data.');
      }

      const result =
        uploadType === 'workspace'
          ? await logoApi.uploadWorkspaceLogo(uploadIdValue, base64, file.type)
          : await logoApi.uploadFormLogo(uploadIdValue, base64, file.type);

      if (!result?.url) {
        throw new Error('Upload succeeded but no URL was returned.');
      }

      onChange(result.url);
    } catch (err) {
      setUploadError(err.message || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  }

  return (
    <div>
      {helperText && (
        <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '0.5rem' }}>
          {helperText}
        </p>
      )}
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          type="url"
          value={value || ''}
          onChange={(e) => {
            setUploadError(null);
            onChange(e.target.value);
          }}
          placeholder="https://yourdomain.com/logo.png"
          className="fb-input"
          style={{ flex: '1 1 220px' }}
          disabled={disabled || uploading}
        />
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handleFilePick}
          disabled={disabled || uploading}
        />
        <button
          type="button"
          className="inbox-pill-btn"
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled || uploading || !uploadIdValue}
        >
          {uploading ? 'Uploading...' : 'Upload'}
        </button>
      </div>
      {uploadError && (
        <p style={{ color: '#dc3545', fontSize: '0.85rem', marginTop: '0.5rem' }}>{uploadError}</p>
      )}
      {value && (
        <div style={{ marginTop: '0.5rem' }}>
          <img
            src={value}
            alt="Logo preview"
            style={{
              maxHeight: '50px',
              maxWidth: '200px',
              border: '1px solid #ddd',
              padding: '4px',
              borderRadius: '4px',
            }}
            onError={(e) => {
              e.target.style.display = 'none';
              if (e.target.nextSibling) {
                e.target.nextSibling.style.display = 'block';
              }
            }}
          />
          <span style={{ display: 'none', color: '#dc3545', fontSize: '0.85rem' }}>
            Image failed to load. Check URL.
          </span>
        </div>
      )}
    </div>
  );
}

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Could not read file.'));
    reader.readAsDataURL(file);
  });
}
